import omit from 'lodash/omit';

import { CustomerContext } from '~src/context/CustomerContext';
import { PublicCalculatorContext } from '~src/context/PublicCalculatorContext';
import { InsertAndSendOffersVariables } from '~src/hooks/graphql/useOffer';
import { FinancingProvider } from '~src/hooks/useFinancingProvider';
import { LeadOrigin } from '~src/hooks/useLeadOrigin';
import {
  AddressInput,
  Customer,
  CustomerDetails,
  DeepPartial,
  OfferProducts,
  ProductInput,
  ProductTypes,
  ResidenceInput,
  RoofInput,
  toCustomerInput,
} from '~src/model';
import { DeepNullToUndefined, heatSourceToFuelType } from '~src/util';

type FormatSendOffersParameters = CustomerContext &
  Pick<PublicCalculatorContext, 'address'> & {
    residence?: Partial<Omit<ResidenceInput, 'address'>>;
    leadOrigin?: LeadOrigin;
    financingProvider?: FinancingProvider;
    selectedProductTypes?: ProductTypes;
    offerProducts?: DeepPartial<OfferProducts>;
  };

export const formatSendOffersParameters = ({
  residence,
  address,
  offerProducts,
  customer,
  leadOrigin = 'nordea',
  financingProvider,
  selectedProductTypes = ['heatPump'],
}: FormatSendOffersParameters): DeepNullToUndefined<InsertAndSendOffersVariables> => {
  const { jyskeBankDataConsent } = customer ?? {};
  const { heatPumpOffer, solarOffer } = formatSendOffersProducts(offerProducts, selectedProductTypes);

  return {
    customer: {
      ...toCustomerInput(customer as Customer)?.customer,
      leadOrigin,
      financingProvider,
    },
    residence: toResidenceInput(residence ?? {}),
    address: toAddressInput(address)!,
    dataConsent:
      leadOrigin === 'jyskebank' ? { partner: 'jyskebank', consented: jyskeBankDataConsent ?? false } : undefined,
    heatPumpOffers: heatPumpOffer ? [heatPumpOffer] : [],
    solarOffers: solarOffer ? [solarOffer] : [],
  };
};

export const customerDetailsFilledOut = ({ firstName, lastName, email, phoneNumber }: CustomerDetails) =>
  !!firstName && !!lastName && !!email && !!phoneNumber;

const formatSendOffersProducts = (
  offerProducts?: DeepPartial<OfferProducts>,
  selectedProductTypes: ProductTypes = ['heatPump', 'solar']
) => {
  const heatPumpOffer = selectedProductTypes.includes('heatPump') ? formatHeatPumpInput(offerProducts) : undefined;

  const solarOffer = selectedProductTypes.includes('solar') ? formatSolarInput(offerProducts) : undefined;

  return { heatPumpOffer, solarOffer };
};

const formatHeatPumpInput = (offerProducts?: DeepPartial<OfferProducts>) => {
  const { heatPumpOutdoorUnit, heatPumpIndoorUnit } = offerProducts ?? {};

  const outdoorUnitInput = toProductInput(heatPumpOutdoorUnit);

  if (!outdoorUnitInput) {
    return undefined;
  }

  const indoorUnitInput = toProductInput(heatPumpIndoorUnit);

  return {
    heatPumpOutdoorProduct: outdoorUnitInput,
    ...(indoorUnitInput && { heatPumpIndoorProduct: indoorUnitInput }),
  };
};

const formatSolarInput = (offerProducts?: DeepPartial<OfferProducts>) => {
  const { solarPanel, inverter, battery } = offerProducts ?? {};

  const solarPanelInput = toProductInput(solarPanel);
  const inverterInput = toProductInput(inverter);

  if (!solarPanelInput || !inverterInput) {
    return undefined;
  }

  const batteryInput = toProductInput(battery);

  return {
    solarPanelProduct: solarPanelInput,
    inverterProduct: inverterInput,
    ...(batteryInput && { batteryProduct: batteryInput }),
  };
};

const toProductInput = ({ id, price, buyPrice, quantity }: Partial<ProductInput> = {}): ProductInput | undefined =>
  id
    ? {
        id,
        price,
        buyPrice,
        quantity,
      }
    : undefined;

export const formatSendOfferProducts = (
  offer: DeepPartial<OfferProducts>,
  selectedProductTypes: ProductTypes = ['heatPump', 'solar']
) => {
  const { heatPumpOffer, solarOffer } = formatSendOffersProducts(offer, selectedProductTypes);

  return {
    ...(heatPumpOffer ?? {}),
    ...(solarOffer ?? {}),
  };
};

export const toResidenceInput = (
  residence: DeepPartial<Omit<ResidenceInput, 'address' | 'customer'>>
): ResidenceInput => ({
  ...omit(residence, ['customer']),
  area: residence.area ?? 0,
  areaHeated: residence.areaHeated ?? 0,
  electricCar: {
    ...residence.electricCar,
    annualKilometersDriven: residence.electricCar?.annualKilometersDriven ?? 0,
    consumptionPerKilometer: residence.electricCar?.consumptionPerKilometer ?? 0,
    percentageCharged: residence.electricCar?.percentageCharged ?? 0,
  },
  energyExpenditure: residence.energyExpenditure ?? 0,
  floors: residence.floors ?? 0,
  heatDistribution: residence.heatDistribution ?? 'radiator',
  primaryHeating: residence.primaryHeating
    ? toHeatingInput(residence.primaryHeating, 'gas')
    : { annualUsage: 0, fuelType: 'gas' },
  secondaryHeating: residence.secondaryHeating?.annualUsage
    ? toHeatingInput(residence.secondaryHeating, 'wood')
    : undefined,
  roofs: residence.roofs?.map(toRoofInput),
  type: residence.type ?? 'villa',
  waterConsumption: residence.waterConsumption ?? 'medium',
});

export const toHeatingInput = (
  heating: DeepPartial<ResidenceInput['primaryHeating']>,
  fallbackFuelType: string
): ResidenceInput['primaryHeating'] => {
  const { annualUsage, fuelType, heatSource } = heating;

  return {
    ...heating,
    annualUsage: annualUsage ?? 0,
    fuelType: fuelType ?? heatSourceToFuelType(heatSource) ?? fallbackFuelType,
  };
};

export const toRoofInput = (roof: DeepPartial<RoofInput> = {}): RoofInput => ({
  ...roof,
  orientation: roof.orientation ?? 0,
  planeDepth: roof.planeDepth ?? 0,
  planeWidth: roof.planeWidth ?? 0,
});

export const toAddressInput = (address?: DeepPartial<AddressInput>): AddressInput | undefined =>
  isAddressInput(address) ? toValidAddressInput(address) : undefined;

export const toAddressInputWithFallback = (address?: PublicCalculatorContext['address']): AddressInput => ({
  city: address?.city ?? '',
  houseNumber: address?.houseNumber ?? '',
  postalCode: address?.postalCode ?? '',
  street: address?.street ?? '',
  door: address?.door,
  floor: address?.floor,
});

const isAddressInput = (address?: DeepPartial<AddressInput>): address is AddressInput =>
  !!address?.city && !!address?.houseNumber && !!address?.postalCode;

export const toValidAddressInput = (input?: Partial<AddressInput>): AddressInput | undefined => {
  const { city, street, postalCode, houseNumber, floor, door } = input ?? {};
  if (!city || !street || !houseNumber || !postalCode) {
    return undefined;
  }

  return { city, street, postalCode, houseNumber, floor, door };
};
